import {
  type CompanyListItem,
  type ContactListItem,
  MESSENGER_LABELS,
  type MessengerChannel,
  type Paginated,
  type ProductItem,
  type StageItem,
} from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { Check, X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { crmCountersQuery } from '@/lib/crm';
import { formatMoney } from '@/lib/format';

/** Вкладки раздела CRM. */
export function CrmNav() {
  const can = useCan();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const counters = useQuery(crmCountersQuery);
  const tabs: { to: string; label: string; badge?: number; alert?: boolean; show?: boolean }[] = [
    { to: '/crm', label: 'Сделки' },
    { to: '/crm/contacts', label: 'Клиенты' },
    { to: '/crm/companies', label: 'Компании' },
    { to: '/crm/chats', label: 'Чаты', badge: counters.data?.unreadChats, alert: true },
    {
      to: '/crm/activities',
      label: 'Дела',
      badge: counters.data?.todayActivities,
      alert: Boolean(counters.data?.overdueActivities),
    },
    { to: '/crm/products', label: 'Товары' },
    { to: '/crm/settings', label: 'Настройки', show: can('crm.settings.manage') },
  ];
  const active = (to: string) =>
    to === '/crm'
      ? pathname === '/crm' || pathname.startsWith('/crm/deals')
      : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <nav
      aria-label="Разделы CRM"
      className="-mx-4 mb-5 overflow-x-auto border-b border-line px-4 lg:-mx-8 lg:px-8"
    >
      <ul className="flex gap-1">
        {tabs
          .filter((t) => t.show !== false)
          .map((t) => (
            <li key={t.to}>
              <Link
                to={t.to}
                aria-current={active(t.to) ? 'page' : undefined}
                className={clsx(
                  'relative inline-flex h-10 items-center gap-1.5 whitespace-nowrap px-3 text-[14px]',
                  active(t.to) ? 'font-semibold text-ink' : 'text-steel hover:text-ink',
                )}
              >
                {t.label}
                {t.badge ? (
                  <span
                    className={clsx(
                      'min-w-[18px] rounded-full px-1.5 text-center text-[11px] font-semibold leading-[17px] tabular-nums',
                      t.alert ? 'bg-danger text-white' : 'bg-sunken text-ink-soft',
                    )}
                  >
                    {t.badge}
                  </span>
                ) : null}
                {active(t.to) && (
                  <span aria-hidden className="absolute inset-x-2 -bottom-px h-[3px] rounded-t bg-signal" />
                )}
              </Link>
            </li>
          ))}
      </ul>
    </nav>
  );
}

export function StageBadge({ stage }: { stage: Pick<StageItem, 'name' | 'color'> }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[12.5px] font-medium"
      style={{ background: `${stage.color}1f`, color: stage.color }}
    >
      <span aria-hidden className="size-2 rounded-full" style={{ background: stage.color }} />
      {stage.name}
    </span>
  );
}

export function Money({
  amount,
  currency = 'RUB',
  className,
}: {
  amount: number | null;
  currency?: string;
  className?: string;
}) {
  if (amount === null) return <span className={clsx('text-steel', className)}>—</span>;
  return <span className={clsx('tabular-nums', className)}>{formatMoney(amount, currency)}</span>;
}

/** Метка мессенджера: «TG» синим, «MAX» фиолетовым — без логотипов. */
export function ChannelMark({ channel, withLabel }: { channel: MessengerChannel; withLabel?: boolean }) {
  return (
    <span
      title={MESSENGER_LABELS[channel]}
      className={clsx(
        'inline-flex h-[18px] items-center rounded px-1 text-[10.5px] font-bold uppercase tracking-wide',
        channel === 'telegram' ? 'bg-[#e3f0fa] text-[#1f6fa8]' : 'bg-[#ece8fb] text-[#5b45b8]',
      )}
    >
      {withLabel ? MESSENGER_LABELS[channel] : channel === 'telegram' ? 'TG' : 'MAX'}
    </span>
  );
}

/** Полоса стадий как в Битриксе: пройденные закрашены, клик — перенос (если можно). */
export function StageBar({
  stages,
  current,
  onPick,
  disabled,
}: {
  stages: StageItem[];
  current: StageItem;
  onPick?: (s: StageItem) => void;
  disabled?: boolean;
}) {
  const open = stages.filter((s) => s.semantic === 'open');
  const finals = stages.filter((s) => s.semantic !== 'open');
  const currentIndex = open.findIndex((s) => s.id === current.id);
  const closed = current.semantic !== 'open';
  return (
    <fieldset className="flex min-w-0 flex-wrap gap-1.5">
      <legend className="sr-only">Стадия сделки</legend>
      <div className="flex w-full min-w-0 gap-1 sm:w-auto sm:flex-1">
        {open.map((s, i) => {
          const passed = closed ? current.semantic === 'won' : i <= currentIndex;
          const isCurrent = s.id === current.id;
          return (
            <button
              key={s.id}
              type="button"
              disabled={disabled || isCurrent}
              onClick={() => onPick?.(s)}
              aria-pressed={isCurrent}
              title={s.name}
              className={clsx(
                'h-8 min-w-0 flex-1 truncate rounded px-2 text-[12.5px] font-medium transition-colors',
                passed ? 'text-white' : 'bg-sunken text-ink-soft hover:bg-line',
                isCurrent && 'ring-2 ring-signal ring-offset-1',
                disabled && !isCurrent && 'cursor-default hover:bg-sunken',
              )}
              style={passed ? { background: s.color } : undefined}
            >
              {s.name}
            </button>
          );
        })}
      </div>
      <div className="flex gap-1">
        {finals.map((s) => {
          const isCurrent = s.id === current.id;
          return (
            <button
              key={s.id}
              type="button"
              disabled={disabled || isCurrent}
              onClick={() => onPick?.(s)}
              aria-pressed={isCurrent}
              className={clsx(
                'inline-flex h-8 items-center gap-1 rounded border px-2.5 text-[12.5px] font-medium',
                isCurrent
                  ? 'border-transparent text-white'
                  : 'border-line-strong bg-surface text-ink-soft hover:bg-sunken',
              )}
              style={isCurrent ? { background: s.color } : undefined}
            >
              {s.semantic === 'won' ? (
                <Check className="size-3.5" aria-hidden />
              ) : (
                <X className="size-3.5" aria-hidden />
              )}
              {s.name}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * Поле выбора с поиском на сервере: контакт, компания, товар.
 * Показывает выбранное значение; крестик очищает.
 */
export function SearchPicker<T extends { id: string }>({
  value,
  onChange,
  fetchItems,
  renderItem,
  cacheKey,
  placeholder,
  id,
  invalid,
  footer,
}: {
  value: { id: string; label: string } | null;
  onChange: (item: T | null) => void;
  fetchItems: (q: string) => Promise<T[]>;
  renderItem: (item: T) => { title: string; hint?: string };
  /** Ключ кэша запросов поиска: 'contacts', 'companies'… */
  cacheKey: string;
  placeholder?: string;
  id?: string;
  invalid?: boolean;
  footer?: ReactNode;
}) {
  const listId = useId();
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(text.trim()), 200);
    return () => window.clearTimeout(t);
  }, [text]);
  const items = useQuery({
    queryKey: ['crm', 'picker', cacheKey, debounced],
    queryFn: () => fetchItems(debounced),
    enabled: open,
  });
  const list = items.data ?? [];

  if (value)
    return (
      <div className="input flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate">{value.label}</span>
        <button
          type="button"
          aria-label="Очистить"
          onClick={() => onChange(null)}
          className="text-steel hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
    );

  const pick = (item: T) => {
    onChange(item);
    setText('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={invalid}
        autoComplete="off"
        className="input"
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, list.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && open && list[active]) {
            e.preventDefault();
            pick(list[active] as T);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && (
        <div
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-line-strong bg-surface py-1 shadow-[0_12px_30px_-12px_rgb(24_34_43/0.45)]"
        >
          {items.isPending ? (
            <p className="px-3 py-2 text-[13px] text-steel">Ищу…</p>
          ) : list.length === 0 ? (
            <p className="px-3 py-2 text-[13px] text-steel">Ничего не найдено</p>
          ) : (
            list.map((item, i) => {
              const r = renderItem(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(item)}
                  className={clsx(
                    'block w-full px-3 py-1.5 text-left',
                    i === active ? 'bg-sunken' : 'hover:bg-sunken',
                  )}
                >
                  <span className="block truncate">{r.title}</span>
                  {r.hint && <span className="block truncate text-[12.5px] text-steel">{r.hint}</span>}
                </button>
              );
            })
          )}
          {footer}
        </div>
      )}
    </div>
  );
}

export const fetchContacts = (q: string) =>
  api<Paginated<ContactListItem>>('/crm/contacts', { query: { q, pageSize: 20 } }).then((r) => r.items);
export const fetchCompanies = (q: string) =>
  api<Paginated<CompanyListItem>>('/crm/companies', { query: { q, pageSize: 20 } }).then((r) => r.items);
export const fetchProducts = (q: string) =>
  api<Paginated<ProductItem>>('/crm/products', { query: { q, pageSize: 20 } }).then((r) => r.items);

export function ContactPicker({
  value,
  onChange,
  id,
  invalid,
}: {
  value: { id: string; label: string } | null;
  onChange: (c: ContactListItem | null) => void;
  id?: string;
  invalid?: boolean;
}) {
  return (
    <SearchPicker<ContactListItem>
      id={id}
      invalid={invalid}
      value={value}
      onChange={onChange}
      fetchItems={fetchContacts}
      cacheKey="contacts"
      placeholder="Имя, телефон или компания"
      renderItem={(c) => ({
        title: c.fullName,
        hint: [c.company?.name, c.phones[0], c.city].filter(Boolean).join(' · ') || undefined,
      })}
    />
  );
}

export function CompanyPicker({
  value,
  onChange,
  id,
  invalid,
}: {
  value: { id: string; label: string } | null;
  onChange: (c: CompanyListItem | null) => void;
  id?: string;
  invalid?: boolean;
}) {
  return (
    <SearchPicker<CompanyListItem>
      id={id}
      invalid={invalid}
      value={value}
      onChange={onChange}
      fetchItems={fetchCompanies}
      cacheKey="companies"
      placeholder="Название или ИНН"
      renderItem={(c) => ({
        title: c.name,
        hint: [c.inn && `ИНН ${c.inn}`, c.city].filter(Boolean).join(' · ') || undefined,
      })}
    />
  );
}

/** Список строк (телефоны, e-mail) — поле + «ещё один». */
export function MultiInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  id,
  invalid,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  type?: string;
  id?: string;
  invalid?: boolean;
}) {
  const rows = value.length ? value : [''];
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((v, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: строки без собственных id, порядок стабилен
        <div key={i} className="flex gap-1.5">
          <input
            id={i === 0 ? id : undefined}
            aria-invalid={invalid}
            className="input"
            type={type}
            value={v}
            placeholder={placeholder}
            onChange={(e) => onChange(rows.map((x, j) => (j === i ? e.target.value : x)))}
          />
          {rows.length > 1 && (
            <button
              type="button"
              aria-label="Убрать"
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-steel hover:bg-sunken hover:text-ink"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      ))}
      {rows.length < 10 && rows[rows.length - 1] && (
        <button type="button" className="link self-start text-[13px]" onClick={() => onChange([...rows, ''])}>
          + ещё
        </button>
      )}
    </div>
  );
}

export function EmptyPanel({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="panel px-6 py-10 text-center">
      <p className="font-medium">{title}</p>
      {children && <div className="mt-2 text-[13.5px] text-steel">{children}</div>}
    </div>
  );
}

export function Pager({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  return (
    <div className="mt-3 flex items-center justify-end gap-2 text-[13.5px]">
      <span className="text-steel tabular-nums">
        {page} из {pages}
      </span>
      <button
        type="button"
        className="link disabled:text-steel"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        Назад
      </button>
      <button
        type="button"
        className="link disabled:text-steel"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      >
        Дальше
      </button>
    </div>
  );
}
